import React, { useRef, useState } from "react";
import useApi from "@/hooks/useApi";
import useSite from "@/hooks/useSite";
import authService from "@/services/AuthService";
import { resolveTimeZone } from "@/utils/formatInSiteTz";
import TimezonePicker from "@/components/TimezonePicker";

interface Props {
  onClose(): void;
}

/** "America/Los_Angeles" -> "Los Angeles" */
export function friendlyTz(tz?: string | null): string {
  if (!tz) return "";
  const last = tz.split("/").pop() ?? tz;
  return last.replace(/_/g, " ");
}

/**
 * Expansive timezone editor shown as a pop-out from the profile menu.
 * Lets the user see exactly which timezone is in effect (and whether it's
 * the site default or their own override), adopt the browser-detected zone
 * in one tap, or pick any zone from the dropdown.
 */
export default function TimezoneModal({ onClose }: Props) {
  const api = useApi();
  const { site } = useSite();
  const ref = useRef<HTMLDivElement>(null);

  const [tz, setTz] = useState<string>(authService.profile?.timeZone ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const siteTz = resolveTimeZone(site?.timezone || undefined);
  const effectiveTz =
    resolveTimeZone(tz || site?.timezone || undefined) ?? browserTz;
  const usingOverride = !!tz;
  const showDetectedSuggestion = !!browserTz && tz !== browserTz;

  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [onClose]);

  async function save(next: string) {
    setSaving(true);
    setError(null);
    try {
      const res = await api.admin.put("Profile/timezone", {
        timeZone: next || null
      });
      if (!res.ok) {
        setError(`Failed (HTTP ${res.status})`);
        return;
      }
      const data = await res.json();
      setTz(data.timeZone ?? "");
      authService.updateProfileTimezone(data.timeZone ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <style>{`
        @keyframes v-tz-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .v-tz-modal { animation: v-tz-in 180ms ease-out both; }
      `}</style>
      <div
        ref={ref}
        className="v-tz-modal absolute bottom-0 left-full ml-2 z-30 w-80 rounded-xl shadow-xl border border-slate-200 bg-white overflow-hidden"
        aria-label="Time zone settings"
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Time zone</span>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1l12 12M13 1L1 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* What's in effect right now */}
          <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Times are shown in
            </div>
            <div className="text-base font-semibold text-slate-800 leading-tight mt-0.5">
              {friendlyTz(effectiveTz)}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {usingOverride
                ? "Your personal override"
                : siteTz
                  ? `Site default (${friendlyTz(siteTz)})`
                  : "Site default"}
            </div>
          </div>

          {/* One-tap: adopt the browser-detected zone */}
          {showDetectedSuggestion && (
            <button
              onClick={() => save(browserTz)}
              disabled={saving}
              className="flex items-center gap-2.5 w-full text-left rounded-lg border border-(--ds-primary)/30 bg-(--ds-primary-bg) px-3 py-2.5 hover:border-(--ds-primary) transition-colors disabled:opacity-50"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--ds-primary)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0"
              >
                <circle cx="12" cy="10" r="3" />
                <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" />
              </svg>
              <span className="text-[12px] leading-tight text-slate-700">
                We detected{" "}
                <span className="font-semibold">{friendlyTz(browserTz)}</span>.{" "}
                <span className="text-(--ds-primary) font-medium">
                  Use this
                </span>
              </span>
            </button>
          )}

          {/* Full picker */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
              Choose a different time zone
            </div>
            <TimezonePicker
              value={tz}
              disabled={saving}
              onChange={(e) => save(e.currentTarget.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white"
            ></TimezonePicker>
          </div>

          {usingOverride && (
            <button
              onClick={() => save("")}
              disabled={saving}
              className="text-[11px] text-(--ds-primary) hover:underline disabled:opacity-50"
            >
              Reset to site default{siteTz ? ` (${friendlyTz(siteTz)})` : ""}
            </button>
          )}
          {error && <div className="text-[11px] text-red-500">{error}</div>}
        </div>
      </div>
    </>
  );
}
